<script setup lang="ts">
/**
 * "ขายต่อ" — the customer panel's sell card (owner-approved mockup 17 ต.ค. 69, LeanNew / LeanReturning):
 *   both          ขั้นตอนการขาย (owner 17 ต.ค. 69 "ลูกค้าใหม่เราต้องถามอะไรเขา 1 2 3 4"): the steps of the customer's
 *                 segment in the owner's order (ตั้งค่า › ขั้นตอนการขาย) — ✓ done with what the customer said · ● the step
 *                 to ask now, its question + "ใส่ในช่องพิมพ์" · ○ later · สรุปยอด → ชำระ greyed until reached. A step the
 *                 owner added is ticked by staff; the others tick themselves from the chat. New customers: what else is
 *                 known (✓ chips, e.g. the web cart) · the pack step shows the pack the system recommends (owner 18 ต.ค.
 *                 69) · every step done → สรุปยอด is the step now: "เปิดลงออเดอร์ (เติมให้แล้ว)" fills the order tab from
 *                 what the chat collected (stores/script.ts fillFromChat — never saved, staff pick the dishes and save)
 *                 คู่มือปิดการขาย (owner 18 ต.ค. 69): the customer replied to the step now with a question / an
 *                 objection → "ลูกค้าถามกลับ: “…” · ตอบตามคู่มือ: ถามตัวเลือก" instead of "ถามไปแล้ว รอลูกค้าตอบ" (the draft box
 *                 has the playbook's answer, or the step card + "ให้ AI พาไปขั้นต่อไป") · ลังเล marks the ทักตาม row
 *                 ทดลอง A/B (owner 18 ต.ค. 69): a chat in the test has a small "ทดลองแบบ A/B" chip (never which variant —
 *                 staff just follow the steps); once the customer's goal is known, the pack / menu step now shows a hint
 *                 for it and the dishes of this round that fit (the approved numbers, as they are)
 *   returning     the upsell line (+ "ใส่ข้อความชวน" = that suggestion into the typing box) · "สั่งชุดเดิมซ้ำ" (the
 *                 order tab, filled) · meals still left on the open pack
 *   both          the AI's cart → "ไปลงออเดอร์" · "ทักตามถ้าลูกค้าเงียบ" (moved here from the old AI block)
 * The data comes from stores/script.ts; nothing here sends a message.
 */
import { computed } from 'vue'
import type { ScriptStep, ScriptStepStatus } from '../../api/script/types'
import type { ConversationDetail } from '../../api/types'
import { followUpTitle } from '../../lib/format'
import { baht } from '../../lib/inbox'
import {
  UPSELL_REASON,
  packSuggestLine,
  prefillSummary,
  replyNote,
  sellFacts,
  sourceLabel,
  stepKind,
  stepsProgress,
  upsellOffer,
} from '../../lib/script'
import { useInbox } from '../../stores/inbox'
import { useScript } from '../../stores/script'
import Icon from '../ui/Icon.vue'
import type { IconName } from '../ui/icons'
import ToggleSwitch from '../ui/ToggleSwitch.vue'

const props = defineProps<{ detail: ConversationDetail }>()
const store = useInbox()
const script = useScript()

const s = computed(() => script.scripts[props.detail.id] ?? null)
const isNew = computed(() => s.value?.segment === 'new')

// ---- ขั้นตอนการขาย
const steps = computed(() => s.value?.steps ?? [])
const asks = computed(() => steps.value.filter((x) => stepKind(x) !== 'system'))
const sys = computed(() => steps.value.filter((x) => stepKind(x) === 'system'))
const progress = computed(() => stepsProgress(steps.value))
/** สรุปยอด → ชำระ on one row: done when both are, current when one of them is. */
const sysStatus = computed<ScriptStepStatus>(() =>
  sys.value.every((x) => x.status === 'done') ? 'done' : sys.value.some((x) => x.status === 'current') ? 'current' : 'system',
)
const ICON: Record<ScriptStepStatus, IconName> = { done: 'checkCircle', current: 'circleDot', todo: 'circle', system: 'circle' }
const STATUS_TH: Record<ScriptStepStatus, string> = { done: 'เสร็จแล้ว', current: 'ถามตอนนี้', todo: 'ยังไม่ถึง', system: 'ยังไม่ถึง' }
const isCustom = (x: ScriptStep) => stepKind(x) === 'custom'
/** The pack the system recommends (the pack step of a new customer, not chosen yet). */
const rec = (x: ScriptStep) => (x.key === 'pack' && x.suggest ? packSuggestLine(x.suggest) : null)
/** The pack step's question IS the recommendation (api pack_offer): the card below shows it — not the sentence twice. */
const offerInQ = (x: ScriptStep) => !!(x.key === 'pack' && x.suggest?.name && x.question?.includes(x.suggest.name))
/** ทดลอง A/B: the customer's goal on the pack / menu step (the dishes list only when the round has the numbers). */
const goalHint = (x: ScriptStep) => ((x.key === 'pack' || x.key === 'menu') && x.goal_hint?.text ? x.goal_hint : null)
const inTest = computed(() => !!s.value?.experiment)
/** สรุปยอด is the step now (every question step done): the order tab can be filled from the chat. */
const summaryNow = computed(() => sys.value.some((x) => x.key === 'summary' && x.status === 'current'))
const prefill = computed(() => s.value?.order_prefill ?? null)
const prefillLine = computed(() => (prefill.value ? prefillSummary(prefill.value) : null))
const busy = (x: ScriptStep) => !!script.ticking[`${props.detail.id}|${x.key}`]
/** New customers: what else is known that no step shows (e.g. the menus of a web cart) — as before, ✓ chips. */
const known = computed(() => {
  if (!isNew.value) return []
  const keys = new Set(steps.value.map((x) => x.key))
  const shown = steps.value.map((x) => x.value ?? '').filter(Boolean)
  return sellFacts(s.value?.known ?? []).filter((f) => !keys.has(f.key) && !shown.some((v) => v.includes(f.value)))
})

function put(x: ScriptStep): void {
  script.insertStep(props.detail.id, x.key)
}
function tick(x: ScriptStep): void {
  void script.tickStep(props.detail.id, x.key, x.status !== 'done')
}

const r = computed(() => s.value?.returning ?? null)
const upsell = computed(() => r.value?.upsell ?? null)
const offer = computed(() => (upsell.value ? upsellOffer(upsell.value) : null))
/** "ใส่ข้อความชวน" needs its suggestion in the list. */
const invite = computed(() => {
  const id = upsell.value?.suggestion_id
  return id ? (script.suggestions(props.detail.id).find((x) => x.id === id) ?? null) : null
})
const repeat = computed(() => (r.value?.repeat_last_order?.available ? r.value.repeat_last_order : null))
const meals = computed(() => (r.value?.open_meals && r.value.open_meals > 0 ? r.value.open_meals : null))
const cart = computed(() => props.detail.ai?.cart ?? null)
const follow = computed(() => props.detail.follow_up)
/** คู่มือปิดการขาย: the customer hesitated ("ขอคิดดูก่อน") — the ทักตาม row says so. */
const hesitated = computed(() => !!s.value?.playbook?.follow_up)
const note = (x: ScriptStep) => (x.customer_reply ? replyNote(x.customer_reply) : null)

/** The invitation into the typing box: after the staff's own words when they typed something (never sent). */
function putInvite(): void {
  const sg = invite.value
  if (!sg) return
  const st = store.composers[props.detail.id]
  const own = !!st?.text.trim() && !((st.origin === 'ai' || st.origin === 'script') && !st.edited)
  if (script.insert(props.detail.id, own ? 'append' : 'replace', sg.id)) store.flash('ใส่ข้อความชวนในช่องพิมพ์แล้ว ตรวจแล้วกดส่ง')
}
</script>

<template>
  <section class="lc sc" aria-label="ขายต่อ">
    <div class="lc-h">
      <Icon name="cart" />
      <span class="lc-t">ขายต่อ</span>
    </div>

    <!-- ขั้นตอนการขาย: what to ask, in the owner's order — ticked from the chat (custom steps: by staff) -->
    <div v-if="steps.length" class="lc-b ss">
      <div class="ss-h">
        <span class="ss-t">{{ isNew ? 'ขั้นตอนลูกค้าใหม่' : 'ขั้นตอนลูกค้าเก่า' }}</span>
        <span v-if="inTest" class="ss-ab" title="แชทนี้อยู่ในการทดลอง A/B ของขั้นตอนการขาย · ทำตามขั้นตามปกติ"
          ><Icon name="shuffle" />ทดลองแบบ A/B</span
        >
        <span v-if="progress.of" class="ss-n mono" :aria-label="`ขั้นที่ ${progress.n} จาก ${progress.of}`">
          {{ progress.n }}/{{ progress.of }}
        </span>
      </div>
      <ol class="ss-list" :aria-label="isNew ? 'ขั้นตอนลูกค้าใหม่' : 'ขั้นตอนลูกค้าเก่า'">
        <li
          v-for="(x, i) in asks"
          :key="x.key"
          class="ss-i"
          :class="x.status"
          :aria-current="x.status === 'current' ? 'step' : undefined"
        >
          <button
            v-if="isCustom(x)"
            type="button"
            role="checkbox"
            class="ss-ic ss-tick"
            :aria-checked="x.status === 'done'"
            :aria-label="`ทำแล้ว: ${x.label}`"
            :title="x.status === 'done' ? 'ติ๊กแล้ว · กดอีกครั้งเพื่อเอาออก' : 'ติ๊กเมื่อทำขั้นนี้แล้ว'"
            :disabled="busy(x)"
            @click="tick(x)"
          >
            <Icon :name="ICON[x.status]" />
          </button>
          <span v-else class="ss-ic"><Icon :name="ICON[x.status]" /></span>
          <span class="ss-b">
            <span class="ss-row">
              <span class="ss-l"
                ><span class="sr">{{ STATUS_TH[x.status] }}: </span><span class="ss-no mono">{{ i + 1 }}</span>{{ x.label }}</span
              >
              <span v-if="x.value" class="ss-v" :title="x.value">{{ x.value }}</span>
            </span>
            <template v-if="x.status === 'current'">
              <span v-if="x.question && !offerInQ(x)" class="ss-q">“{{ x.question }}”</span>
              <span v-if="rec(x)" class="ss-rec" aria-label="แพ็กที่ระบบแนะนำ">
                <span class="ss-rec-h"><Icon name="sparkle" /><span>แนะนำ <b>{{ rec(x)!.name }}</b></span></span>
                <span v-if="rec(x)!.price" class="ss-rec-p">{{ rec(x)!.price }}</span>
                <span v-if="rec(x)!.why" class="ss-rec-w">{{ rec(x)!.why }}</span>
              </span>
              <span v-if="goalHint(x)" class="ss-goal" aria-label="เป้าหมายของลูกค้า">
                <span class="ss-goal-h"><Icon name="target" /><span>{{ goalHint(x)!.text }}</span></span>
                <template v-if="goalHint(x)!.dishes.length">
                  <span v-if="goalHint(x)!.dishes_title" class="ss-goal-t">{{ goalHint(x)!.dishes_title }}</span>
                  <ol class="ss-goal-l">
                    <li v-for="d in goalHint(x)!.dishes.slice(0, 3)" :key="d.menu_id">
                      <span class="ss-goal-n">{{ d.name }}</span><span class="ss-goal-v">{{ d.value }}</span>
                    </li>
                  </ol>
                </template>
              </span>
              <span v-if="x.note" class="ss-asked">{{ x.note }}</span>
              <span v-else-if="note(x)" class="ss-reply" :class="{ out: !x.customer_reply!.in_playbook }" aria-label="ลูกค้าตอบกลับ"
                ><Icon name="reply" /><span
                  ><b>{{ note(x)!.lead }}:</b> <span class="ss-reply-q">{{ note(x)!.quote }}</span> ·
                  {{ note(x)!.how }}</span
                ></span
              >
              <span v-else-if="x.asked" class="ss-asked">ถามไปแล้ว รอลูกค้าตอบ</span>
              <span class="ss-act">
                <button v-if="x.question" type="button" class="lb pri" @click="put(x)"><Icon name="arrowDown" />ใส่ในช่องพิมพ์</button>
                <button v-if="isCustom(x)" type="button" class="lb" :disabled="busy(x)" @click="tick(x)"><Icon name="check" />ทำแล้ว</button>
              </span>
            </template>
          </span>
        </li>
        <li v-if="sys.length" class="ss-i ss-sys" :class="sysStatus" :aria-current="sysStatus === 'current' ? 'step' : undefined">
          <span class="ss-ic"><Icon :name="ICON[sysStatus]" /></span>
          <span class="ss-b">
            <span class="ss-l"
              ><span class="sr">{{ STATUS_TH[sysStatus] }}: </span
              ><template v-for="(x, i) in sys" :key="x.key"
                ><Icon v-if="i" name="arrowRight" /><span :class="{ ok: x.status === 'done' }">{{ x.label }}</span></template
              ></span
            >
            <template v-if="summaryNow && prefill">
              <span v-if="prefillLine" class="ss-pre">{{ prefillLine }}</span>
              <span class="ss-act">
                <button type="button" class="lb pri" :disabled="!!script.repeating[detail.id]" @click="script.fillFromChat(detail.id)">
                  <Icon name="cart" />เปิดลงออเดอร์ (เติมให้แล้ว)
                </button>
              </span>
              <span class="ss-pre-n">เลือกเมนูในแท็บลงออเดอร์ ตรวจแล้วกดบันทึก ระบบไม่บันทึกเอง</span>
            </template>
          </span>
        </li>
      </ol>
      <ul v-if="known.length" class="sc-chips ss-known" aria-label="ข้อมูลลูกค้าอื่นๆ">
        <li v-for="f in known" :key="`k-${f.key}`" class="have" :title="`${f.label} · ${sourceLabel(f.source)}`">
          <Icon name="tick" /><span class="sr">รู้แล้ว {{ f.label }}: </span>{{ f.value }}
        </li>
      </ul>
    </div>

    <!-- returning: upsell -->
    <div v-if="upsell && offer" class="lc-b sc-up">
      <p class="sc-up-t">{{ upsell.detail || UPSELL_REASON[upsell.reason] }}</p>
      <p class="sc-up-s">
        เสนอ <b>{{ offer.to }}</b><template v-if="offer.extra"> · {{ offer.extra }}</template>
      </p>
      <div class="sc-act">
        <button v-if="invite" type="button" class="lb pri" @click="putInvite"><Icon name="arrowDown" />ใส่ข้อความชวน</button>
        <button v-if="repeat" type="button" class="lb" :disabled="!!script.repeating[detail.id]" @click="script.repeatOrder(detail.id)">
          <Icon name="repeat" />สั่งชุดเดิมซ้ำ
        </button>
      </div>
    </div>
    <div v-else-if="repeat" class="lc-b sc-rep">
      <span class="sc-rep-t">{{ repeat.summary }}</span>
      <button type="button" class="lb" :disabled="!!script.repeating[detail.id]" @click="script.repeatOrder(detail.id)">
        <Icon name="repeat" />สั่งชุดเดิมซ้ำ
      </button>
    </div>
    <div v-if="meals" class="lc-b sc-left">
      <span class="sc-meals">เหลือ {{ meals }} มื้อ</span><span>จากแพ็กเดิม · ถามว่าจะรวมส่งรอบหน้าไหม</span>
    </div>

    <!-- the AI's cart (round 9: comes with a draft) -->
    <div v-if="cart" class="lc-b aic">
      <span class="cart-ai"><Icon name="sparkle" /></span>
      <span class="aic-b">
        <span class="aic-t">{{ cart.title }}</span>
        <span class="aic-sub">{{ cart.package }} · {{ cart.source }} · <span class="mono">{{ baht(cart.total) }}</span></span>
      </span>
      <button type="button" class="btn-go" @click="store.panelTab = 'order'"><span>ไปลงออเดอร์</span><Icon name="arrowRight" /></button>
    </div>

    <!-- follow up when the customer goes quiet (was in the AI block) -->
    <div v-if="!detail.is_history" class="lc-b sc-follow">
      <span class="sc-f-b">
        <span class="sc-f-t">{{ followUpTitle(follow.after_minutes) }}</span>
        <span v-if="hesitated" class="sc-f-hes"
          >ลูกค้าขอคิดดูก่อน{{ follow.enabled ? ' · ควรทักตามถ้าเงียบ' : ' · เปิดทักตามไว้ดีไหม' }}</span
        >
        <span class="sc-f-n">{{ follow.enabled ? 'เข้าแท็บทักตามเมื่อถึงเวลา ให้พนักงานกดส่ง' : 'ปิดอยู่ ไม่ทักตาม' }}</span>
      </span>
      <button
        class="st-sw"
        role="switch"
        :aria-checked="follow.enabled"
        :aria-label="followUpTitle(follow.after_minutes)"
        @click="store.toggleFollowUp(detail.id)"
      >
        <ToggleSwitch :on="follow.enabled" size="lg" />
      </button>
    </div>
  </section>
</template>

<style scoped>
.sc {
  border-color: #cfe3d8;
}
.sc .lc-h {
  background: var(--acs);
}
.sc .lc-h > :deep(.ic) {
  color: var(--ac);
}
/* ---- ขั้นตอนการขาย */
.ss-h {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.ss-t {
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  color: var(--mut);
}
.ss-n {
  flex: none;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: var(--ac2);
}
.ss-list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.ss-i {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
}
.ss-i.current {
  margin: 2px -8px;
  padding: 7px 8px 8px;
  border-radius: 9px;
  background: var(--acs);
}
.ss-ic {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 21px;
  color: #b4bdb8;
}
.ss-ic :deep(.ic) {
  width: 17px;
  height: 17px;
  stroke-width: 2;
}
.done > .ss-ic,
.current > .ss-ic {
  color: var(--ac);
}
.ss-tick {
  padding: 0;
  border-radius: 6px;
}
.ss-tick:hover:not(:disabled) {
  background: #e8efeb;
}
.ss-tick:disabled {
  opacity: 0.6;
}
.ss-b {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.ss-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 10px;
}
.ss-l {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13.5px;
  line-height: 21px;
  color: var(--ink);
}
.current .ss-l {
  font-weight: 600;
}
.todo .ss-l,
.ss-sys .ss-l {
  color: var(--mut);
}
.ss-no {
  margin-right: 6px;
  font-size: 12px;
  color: var(--mut);
}
.ss-v {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 21px;
  color: var(--ac2);
}
.current .ss-v {
  color: var(--warn);
}
.ss-q {
  margin-top: 3px;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.ss-asked {
  margin-top: 2px;
  font-size: 12px;
  line-height: 18px;
  color: var(--warn);
}
/* คู่มือปิดการขาย: the customer replied to the step with a question / an objection */
.ss-reply {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 4px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.ss-reply b {
  font-weight: 600;
  color: var(--ac2);
}
.ss-reply.out b {
  color: var(--warn);
}
.ss-reply :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  color: var(--ac);
}
.ss-reply.out :deep(.ic) {
  color: var(--warn);
}
.ss-reply-q {
  font-weight: 600;
}
.ss-act {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
}
.ss-sys .ss-l {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 6px;
}
.ss-sys .ss-l :deep(.ic) {
  width: 13px;
  height: 13px;
  color: #b4bdb8;
}
.ss-sys .ok {
  color: var(--ac2);
  font-weight: 600;
}
.ss-sys.current .ss-l {
  color: var(--ink);
  font-weight: 600;
}
.ss-known {
  margin-top: 8px;
}
/* the pack the system recommends (new customer, pack step now) */
.ss-rec {
  display: flex;
  flex-direction: column;
  margin-top: 6px;
  padding: 7px 9px;
  border-radius: 8px;
  border: 1px solid #cfe3d8;
  background: #fff;
}
.ss-rec-h {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
}
.ss-rec-h :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  color: var(--ac);
}
.ss-rec-h b {
  font-weight: 600;
}
.ss-rec-p {
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  color: var(--ac2);
}
.ss-rec-w {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  overflow-wrap: anywhere;
}
/* ทดลอง A/B: the chip of a chat in the test + the customer's goal on the pack / menu step */
.ss-ab {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: #eef1ef;
  color: #3e4d46;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
.ss-ab :deep(.ic) {
  width: 12px;
  height: 12px;
}
.ss-goal {
  display: flex;
  flex-direction: column;
  margin-top: 6px;
  padding: 7px 9px;
  border-radius: 8px;
  border: 1px solid #cfe3d8;
  background: #fff;
}
.ss-goal-h {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.ss-goal-h :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  color: var(--ac);
}
.ss-goal-t {
  margin-top: 5px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: var(--mut);
}
.ss-goal-l {
  margin: 2px 0 0;
  padding: 0;
  list-style: none;
}
.ss-goal-l li {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 8px;
  font-size: 12.5px;
  line-height: 19px;
}
.ss-goal-n {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.ss-goal-v {
  flex: none;
  font-weight: 600;
  color: var(--ac2);
  white-space: nowrap;
}
/* สรุปยอด now: what goes into the order tab + the button */
.ss-pre {
  margin-top: 3px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.ss-pre-n {
  margin-top: 4px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.sc-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.have {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 9px;
  border-radius: 13px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  white-space: nowrap;
}
.have {
  background: var(--acs);
  color: var(--ac2);
}
.have :deep(.ic) {
  width: 12px;
  height: 12px;
  stroke-width: 3;
}
.sc-up-t {
  font-size: 13.5px;
  line-height: 21px;
}
.sc-up-s {
  margin-top: 2px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.sc-up-s b {
  color: var(--ink);
  font-weight: 600;
}
.sc-act {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.sc-rep {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sc-rep-t {
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.sc-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-size: 12.5px;
  line-height: 19px;
}
.sc-meals {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 9px;
  border-radius: 12px;
  background: var(--warns);
  color: var(--warn);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.aic {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f7fbf9;
}
.cart-ai {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: #17211c;
  color: #86e3bc;
  flex: none;
}
.cart-ai :deep(.ic) {
  width: 14px;
  height: 14px;
}
.aic-b {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.aic-t {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 21px;
}
.aic-sub {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.btn-go {
  flex: none;
  height: 34px;
  padding: 0 10px 0 12px;
  border-radius: 9px;
  border: 1px solid #9fcdb8;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  background: #fff;
  color: var(--ac2);
}
.btn-go:hover {
  background: var(--acs);
}
.btn-go :deep(.ic) {
  width: 15px;
  height: 15px;
}
.sc-follow {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sc-f-b {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.sc-f-t {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 21px;
}
.sc-f-hes {
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: var(--warn);
}
.sc-f-n {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.st-sw {
  flex: none;
  display: inline-flex;
  padding: 2px;
  border-radius: 999px;
}
</style>
